import { Checkbox } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { MovementTypes } from "constants/index";
import { getClientName } from "helpers/getClientName";
import { getMovementArticleStockStateLabel } from "helpers/stockStatus";

type CustomGridColDef = GridColDef & {
  order?: number;
  visibilityFlag?: "show_lote" | "show_oc" | "show_fecha_vencimiento";
};

type CustomGridProps = {
  name: string;
  labelName: string;
  fields: CustomGridColDef[];
  extraFields?: { [key in MovementTypes]?: CustomGridColDef[] };
};

export const gridStructure: { [key: string]: CustomGridProps } = {
  users: {
    name: "users",
    labelName: "Usuarios",
    fields: [
      {
        field: "first_name",
        headerName: "Nombre",
        headerAlign: "center",
        type: "string",
        flex: 1,
      },
      {
        field: "last_name",
        headerName: "Apellido",
        headerAlign: "center",
        type: "string",
        flex: 1,
      },
      {
        field: "email",
        headerName: "Email",
        headerAlign: "center",
        type: "string",
        flex: 1,
      },
      {
        field: "role",
        headerName: "Rol",
        headerAlign: "center",
        type: "string",
        flex: 1,
      },
      {
        field: "is_active",
        headerName: "Activo",
        headerAlign: "center",
        type: "boolean",
        flex: 1,
        editable: false,
        renderCell: (params) => (
          <Checkbox
            checked={params.value}
            onClick={() => {
              params.value = !params.value;
            }}
            disabled
          />
        ),
      },
    ],
  },
  clients: {
    name: "clients",
    labelName: "Clientes",
    fields: [
      {
        field: "code",
        headerName: "Codigo",
        headerAlign: "center",
        type: "string",
      },
      {
        field: "razon_social",
        headerName: "Razón social",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "address",
        headerName: "Dirección",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "cuit",
        headerName: "CUIT",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "contact",
        headerName: "Contacto",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "observations",
        headerName: "Observaciones",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "parent_client_name",
        headerName: "Cliente Padre",
        headerAlign: "center",
        type: "string",
        flex: 1,
      },
      {
        field: "is_active",
        headerName: "Activo",
        headerAlign: "center",
        type: "boolean",
        flex: 1,
        editable: false,
        renderCell: (params) => <Checkbox checked={params.value} disabled />,
      },
    ],
  },
  associated_clients: {
    name: "associated_clients",
    labelName: "Clientes asociados",
    fields: [
      {
        field: "razon_social",
        headerName: "Razón social",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "client",
        valueGetter: (params) => getClientName(params.row.client),
        headerName: "Asociado a",
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
      },
      {
        field: "address",
        headerName: "Dirección",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "cuit",
        headerName: "CUIT",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "contact",
        headerName: "Contacto",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "is_active",
        headerName: "Activo",
        headerAlign: "center",
        type: "boolean",
        flex: 1,
        editable: false,
        renderCell: (params) => <Checkbox checked={params.value} disabled />,
      },
    ],
  },
  articles: {
    name: "articles",
    labelName: "Artículos",
    fields: [
      {
        field: "code",
        headerName: "Codigo",
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
      },
      {
        field: "description",
        headerName: "Descripción",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "amount",
        headerName: "Stock",
        headerAlign: "center",
        type: "number",
        editable: true,
        flex: 1,
      },
      {
        field: "weight",
        headerName: "Peso",
        headerAlign: "center",
        type: "number",
        editable: true,
        flex: 1,
      },
      {
        field: "width",
        headerName: "Ancho",
        headerAlign: "center",
        type: "number",
        editable: true,
        flex: 1,
      },
      {
        field: "height",
        headerName: "Alto",
        headerAlign: "center",
        type: "number",
        editable: true,
        flex: 1,
      },
      {
        field: "long",
        headerName: "Largo",
        headerAlign: "center",
        type: "number",
        editable: true,
        flex: 1,
      },
      {
        field: "amount_per_container",
        valueGetter: (params) => {
          return params.row.amount_per_container;
        },
        headerName: "Cantidad por Contenedor",
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
      },
      {
        field: "unit",
        valueGetter: (params) => {
          return params.row.unit.description;
        },
        headerName: "Unidad Operativa",
        headerAlign: "center",
        type: "number",
        editable: false,
        flex: 1,
      },
      {
        field: "presentation",
        valueGetter: (params) => params.row.presentation ?? "-",
        headerName: "Presentación",
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
      },
      {
        field: "units_per_pallet",
        valueGetter: (params) => {
          return params.row.units_per_pallet;
        },
        headerName: "Unidades por pallet",
        headerAlign: "center",
        type: "number",
        editable: false,
        flex: 1,
      },
      {
        field: "container",
        valueGetter: (params) => {
          return params.row.container.description;
        },
        headerName: "Contenedor",
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
      },
      {
        field: "client",
        valueGetter: (params) => getClientName(params.row.client),
        headerName: "Cliente",
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
      },
      {
        field: "observation",
        valueGetter: (params) => {
          return params.row.observation?.trim() || "-";
        },
        headerName: "Observaciones",
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
      },
      {
        field: "require_fecha_vencimiento",
        headerName: "Requiere FV",
        headerAlign: "center",
        align: "center",
        type: "string",
        flex: 1,
        valueGetter: (params) => {
          const value = params.row.require_fecha_vencimiento;
          return value ? "Sí" : "No";
        },
      },
      {
        field: "is_standard_amount_per_container",
        headerName: "Cant. por Cont. Estandar",
        headerAlign: "center",
        align: "center",
        type: "string",
        flex: 1,
        valueGetter: (params) => {
          const value = params.row.is_standard_amount_per_container;
          return value ? "Sí" : "No";
        },
      },
      {
        field: "group_codes",
        headerName: "Grupo",
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
        valueGetter: (params) => params.row.group_codes ?? "-",
      },
    ],
  },
  containers: {
    name: "containers",
    labelName: "Contenedores",
    fields: [
      {
        field: "description",
        headerName: "Descripción",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "m3_min",
        headerName: "M3 mínimo",
        headerAlign: "center",
        type: "number",
        editable: true,
        flex: 1,
      },
      {
        field: "m3_max",
        headerName: "M3 máximo",
        headerAlign: "center",
        type: "number",
        editable: true,
        flex: 1,
      },
      {
        field: "volume_weight",
        headerName: "Peso Volumétrico",
        headerAlign: "center",
        type: "number",
        editable: false,
        flex: 1,
      },
      {
        field: "is_active",
        headerName: "Activo",
        headerAlign: "center",
        type: "boolean",
        flex: 1,
        editable: false,
        renderCell: (params) => <Checkbox checked={params.value} disabled />,
      },
    ],
  },
  units: {
    name: "units",
    labelName: "Unidades",
    fields: [
      {
        field: "description",
        headerName: "Descripción",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "is_active",
        headerName: "Activo",
        headerAlign: "center",
        type: "boolean",
        flex: 1,
        editable: false,
        renderCell: (params) => <Checkbox checked={params.value} disabled />,
      },
    ],
  },
  client_settlement_types: {
    name: "client_settlement_types",
    labelName: "Preferencias de Liquidacion",
    fields: [
      {
        field: "Tipo de liquidación",
        headerName: "Tipo de liquidación",
        headerAlign: "center",
        maxWidth: 10,
        type: "string",
        editable: false,
      },
      {
        field: "is_default",
        headerName: "Default",
        headerAlign: "center",
        type: "boolean",
        editable: true,
        flex: 1,
        renderCell: (params) => <Checkbox checked={params.value} disabled />,
      },
      {
        field: "Precio",
        headerName: "Precio",
        headerAlign: "center",
        type: "number",
        editable: true,
        flex: 1,
      },
    ],
  },
  stores: {
    name: "stores",
    labelName: "Almacenes",
    fields: [
      {
        field: "code",
        headerName: "Código",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "description",
        headerName: "Descripción",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "is_active",
        headerName: "Activo",
        headerAlign: "center",
        type: "boolean",
        flex: 1,
        editable: false,
        renderCell: (params) => <Checkbox checked={params.value} disabled />,
      },
    ],
  },
  additional_items: {
    name: "additional_items",
    labelName: "Artículos adicionales",
    fields: [
      {
        field: "description",
        headerName: "Descripción",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "client",
        headerName: "Cliente",
        valueGetter: (params) => {
          const { client, client_id } = params.row;
          return client_id === null ? "TODOS" : getClientName(client);
        },
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
      },
      {
        field: "price",
        headerName: "Precio",
        headerAlign: "center",
        type: "number",
        editable: true,
        flex: 1,
      },
      {
        field: "time_minutes",
        headerName: "Tiempo",
        headerAlign: "center",
        type: "number",
        editable: true,
        flex: 1,
      },
    ],
  },
  groups: {
    name: "groups",
    labelName: "Grupos de artículos",
    fields: [
      {
        field: "code",
        headerName: "Código",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "description",
        headerName: "Descripción",
        headerAlign: "center",
        type: "string",
        editable: true,
        flex: 1,
      },
      {
        field: "client",
        headerName: "Cliente",
        valueGetter: (params) => {
          const { client, client_id } = params.row;
          return client_id === null ? "TODOS" : getClientName(client);
        },
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
      },
    ],
  },
  movement_articles: {
    name: "movement_articles",
    labelName: "MovementArticles",
    fields: [
      {
        field: "code",
        headerName: "Código",
        headerAlign: "center",
        type: "string",
        valueGetter: (params) => {
          return params.row.article_locations.article.code;
        },
        editable: false,
        flex: 1,
        order: 1,
      },
      {
        field: "amount",
        headerName: "Cantidad",
        headerAlign: "center",
        editable: true,
        type: "number",
        flex: 1,
        order: 2,
      },
      {
        field: "description",
        headerName: "Descripción",
        headerAlign: "center",
        flex: 1,
        editable: false,
      },
      {
        field: "amount_per_container",
        headerName: "Cantidad por Contenedor",
        headerAlign: "center",
        editable: true,
        type: "number",
        valueGetter: (params) => {
          return params.row.amount_per_container;
        },
        flex: 1,
        order: 4,
      },
      {
        field: "amount_per_pallets",
        headerName: "Pallets totales",
        headerAlign: "center",
        type: "number",
        valueGetter: (params) => {
          const amount = params.row.amount;
          const unitsPerPallet =
            params.row.article_locations.article.units_per_pallet;
          return unitsPerPallet ? Math.ceil(amount / unitsPerPallet) : 0;
        },
        editable: false,
        flex: 1,
      },
      {
        field: "unit",
        headerName: "UM",
        headerAlign: "center",
        editable: false,
        flex: 1,
        type: "string",
      },
      {
        field: "presentation",
        headerName: "Presentación",
        type: "string",
        flex: 1,
        valueGetter: (params) =>
          params.row.presentation ?? "-",
      },
      {
        field: "lote",
        headerName: "Lote",
        headerAlign: "center",
        editable: true,
        flex: 1,
        visibilityFlag: "show_lote",
        valueGetter: (params) => params.row.article_locations?.lote ?? "",
      },
      {
        field: "oc",
        headerName: "OC",
        headerAlign: "center",
        editable: true,
        flex: 1,
        visibilityFlag: "show_oc",
        valueGetter: (params) => params.row.article_locations?.oc ?? "",
      },
      {
        field: "fecha_vencimiento",
        headerName: "Fecha de Vencimiento",
        headerAlign: "center",
        type: "date",
        editable: true,
        flex: 1,
        visibilityFlag: "show_fecha_vencimiento",
        valueGetter: (params) => {
          const v = params.row.article_locations?.fecha_vencimiento;
          return v ? new Date(v) : null;
        },
      },
      {
        field: "location.code",
        valueGetter: (params) => {
          return params.row.article_locations.location.code;
        },
        headerName: "Ubicación",
        headerAlign: "center",
        editable: false,
        flex: 1,
      },
      {
        field: "observation",
        headerName: "Observación",
        headerAlign: "center",
        editable: true,
        flex: 1,
        valueGetter: (params) =>
          params.row.article_locations?.observation_available ??
          params.row.article_locations?.observation_not_available ??
          null,
      },
      {
        field: "subtotal_kg",
        headerName: "Subtotal (Kg)",
        headerAlign: "center",
        flex: 1,
        editable: false,
      },
      {
        field: "subtotal",
        headerName: "Subtotal (Un)",
        valueGetter: (params) => {
          return params.row.subtotal;
        },
        headerAlign: "center",
        editable: false,
        flex: 1,
      },
    ],
    extraFields: {
      [MovementTypes.Discharge]: [
        {
          field: "remaining_stock",
          headerName: "Cantidad Restante",
          headerAlign: "center",
          flex: 1,
          editable: false,
          valueGetter: (params) => {
            let stockUsedInMovement = 0;
            params.api.getAllRowIds().forEach((id) => {
              const row = params.api.getRow(id);
              if (
                row.article_locations.id === params.row.article_locations.id &&
                !row.is_reserved
              ) {
                stockUsedInMovement += row.amount;
              }
            });
            const { stock, reserved, not_available, in_work_orders } = params.row.article_locations;
            return stock - reserved - (not_available ?? 0) - (in_work_orders ?? 0) - stockUsedInMovement;
          },
          order: 3,
        },
      ],
      [MovementTypes.Preparation]: [
        {
          field: "remaining_stock",
          headerName: "Cantidad Restante",
          headerAlign: "center",
          flex: 1,
          editable: false,
          valueGetter: (params) => {
            let stockUsedInMovement = 0;
            params.api.getAllRowIds().forEach((id) => {
              const row = params.api.getRow(id);
              if (
                row.article_locations.id === params.row.article_locations.id &&
                !row.is_reserved
              ) {
                stockUsedInMovement += row.amount;
              }
            });
            const { stock, reserved, not_available, in_work_orders } = params.row.article_locations;
            return stock - reserved - (not_available ?? 0) - (in_work_orders ?? 0) - stockUsedInMovement;
          },
          order: 3,
        },
      ],
      [MovementTypes.Income]: [
        {
          field: "container",
          headerName: "Contenedor",
          headerAlign: "center",
          editable: false,
          flex: 1,
          order: 3,
        },
        {
          field: "stock_state",
          headerName: "Estado mercadería",
          headerAlign: "center",
          editable: false,
          flex: 1,
          order: 4,
          valueGetter: (params) =>
            getMovementArticleStockStateLabel(params.row.stock_state),
        },
      ],
      [MovementTypes.Transfer]: [],
    },
  },
  movement_additional_items: {
    name: "movement_additional_items",
    labelName: "MovementAdditionalItems",
    fields: [
      {
        field: "additional_item_id",
        headerName: "Código",
        headerAlign: "center",
        editable: false,
        flex: 1,
      },
      {
        field: "description",
        headerName: "Descripción",
        headerAlign: "center",
        flex: 1,
        editable: false,
      },
      {
        field: "amount",
        headerName: "Cantidad",
        headerAlign: "center",
        flex: 1,
        editable: false,
      },
    ],
  },
  locations: {
    name: "locations",
    labelName: "Ubicaciones",
    fields: [
      {
        field: "code",
        headerName: "Código",
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
      },
      {
        field: "store_code",
        headerName: "Almacen",
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
      },
      {
        field: "location_type",
        headerName: "Tipo",
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
      },
      {
        field: "is_hidden",
        headerName: "Oculta para clientes",
        headerAlign: "center",
        type: "boolean",
        editable: false,
        flex: 1,
      },
      {
        field: "width",
        headerName: "Ancho",
        headerAlign: "center",
        type: "number",
        editable: true,
        flex: 1,
      },
      {
        field: "height",
        headerName: "Alto",
        headerAlign: "center",
        type: "number",
        editable: true,
        flex: 1,
      },
      {
        field: "long",
        headerName: "Largo",
        headerAlign: "center",
        type: "number",
        editable: true,
        flex: 1,
      },
    ],
  },
  sublocations: {
    name: "locations",
    labelName: "Ubicaciones",
    fields: [
      {
        field: "code",
        headerName: "Código",
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
      },
      {
        field: "parent_code",
        headerName: "Ubicación Padre",
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
      },
      {
        field: "store_code",
        headerName: "Almacen",
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
      },
      {
        field: "location_type",
        headerName: "Tipo",
        headerAlign: "center",
        type: "string",
        editable: false,
        flex: 1,
      },
      {
        field: "width",
        headerName: "Ancho",
        headerAlign: "center",
        type: "number",
        editable: true,
        flex: 1,
      },
      {
        field: "height",
        headerName: "Alto",
        headerAlign: "center",
        type: "number",
        editable: true,
        flex: 1,
      },
      {
        field: "long",
        headerName: "Largo",
        headerAlign: "center",
        type: "number",
        editable: true,
        flex: 1,
      },
    ],
  },
};
